import Table from "@/components/shared-ui/Table";
import type { AirtableSLARow } from "@/services/api/types";
import { createColumnHelper } from "@tanstack/react-table";
import * as Styled from "./styled";
import { useMediaQuery } from "@/hooks/useMatchMedia";
import { statusColours } from "@/styles/statusColours";

type Props = {
  columns: string[];
  data: AirtableSLARow[];
};

const SLATable = ({ data, columns }: Props) => {
  const columnHelper = createColumnHelper<AirtableSLARow>();
  const isDesktop = useMediaQuery("large");

  function determineColor(targetValue: string, actualValue: string) {
    if (actualValue === "N/A" || !targetValue.includes("%")) {
      return undefined;
    }
    // Convert the percentage strings to numbers
    const actual = parseFloat(actualValue);

    // Determine the color based on the criteria
    if (actual >= 89.9) {
      return statusColours.green;
    } else if (actual <= 79.8) {
      return statusColours.red;
    } else {
      return statusColours.orange;
    }
  }

  const actualsFormatter = (row: AirtableSLARow, value: string | undefined) => {
    if (!value) {
      return undefined;
    }

    const valueColor = determineColor(row.Target, value);

    return (
      <Styled.SLAColor color={valueColor?.backgroundColour}>
        {valueColor && <Styled.ProgressCircle color={valueColor.colour} />}
        <span>{value}</span>
      </Styled.SLAColor>
    );
  };

  const defaultColumns = columns.map((col) => {
    if (col === "Metric") {
      return columnHelper.accessor(col, {
        header: () => col,
        cell: (info) => info.getValue(),
        size: isDesktop ? 0 : 300,
      });
    }
    if (col.includes("Actuals")) {
      return columnHelper.accessor((row) => row[col], {
        id: `${col}`,
        header: () => (
          <span
            style={{
              width: "20px",
              whiteSpace: "break-spaces",
              display: "flex",
            }}
          >
            {col}
          </span>
        ),
        cell: (props) => actualsFormatter(props.row.original, props.getValue()),
        size: isDesktop ? 130 : 100,
      });
    }
    return columnHelper.accessor(col, {
      header: () => (
        <span
          style={{ width: "20px", whiteSpace: "break-spaces", display: "flex" }}
        >
          {col}
        </span>
      ),
      cell: (info) => info.getValue(),
      size: isDesktop ? 130 : 100,
    });
  });

  return <Table columns={defaultColumns} data={data} fixedLayout />;
};

export default SLATable;
